import type { ComponentProps } from 'react'

import { Image } from '@adverity/design-system/components/image'

import { assetsMap, responsiveAnimations } from './generated/assets'
import type { StaticAsset } from './generated/assets'

export type { StaticAsset, RawStaticAsset } from './generated/assets'

type Props = (
    | {
          src: StaticAsset
          maxWidth: ComponentProps<typeof Image>['maxWidth']
      }
    | {
          src: StaticAsset
          maxHeight: ComponentProps<typeof Image>['maxHeight']
      }
    | {
          src: StaticAsset
          width: ComponentProps<typeof Image>['width']
          height?: ComponentProps<typeof Image>['height']
      }
) &
    Omit<ComponentProps<typeof Image>, 'src' | 'width' | 'height' | 'maxWidth'>

/**
 * Returns the URL for a static image asset.
 *
 * @param src - The static asset identifier from the generated assets map
 * @returns The URL path to the static image
 *
 * @example
 * const url = staticImageUrl('Adverity-2023-Illustration_ACupOfJoe.svg')
 * // Returns: '/static/images/acup-of-joe.svg'
 */
export const staticImageUrl = (src: StaticAsset) => assetsMap[src]

/**
 * Returns the responsive animation URLs for different screen sizes.
 *
 * @param src - The responsive animation asset identifier
 * @returns Object containing URLs for desktop, tablet, mobile, and static versions
 */
const responsiveAnimationsUrl = (src: keyof typeof responsiveAnimations) => responsiveAnimations[src]

/**
 * React component for rendering static images from the generated assets map.
 *
 * @param props - Component props including src (StaticAsset), alt text, and other Image component props
 * @returns Image component with resolved static asset URL
 *
 * @example
 * <StaticImage
 *   src="Adverity-2023-Illustration_ACupOfJoe.svg"
 *   alt="Coffee illustration"
 *   maxWidth={200}
 * />
 */
export const StaticImage = ({ alt, src, ...props }: Props) => <Image {...props} alt={alt} src={staticImageUrl(src)} />

/**
 * React component for rendering responsive WebP images with fallbacks for different screen sizes.
 * Uses the HTML picture element with multiple source elements for responsive delivery.
 *
 * @param props - Component props including src (responsive animation key) and Image component props
 * @returns Picture element with responsive image sources
 *
 * @example
 * <ResponsiveWebpImage
 *   src="dashboard-animation"
 *   alt="Dashboard animation"
 * />
 */
export const ResponsiveWebpImage = ({
    src,
    ...imgProps
}: {
    src: keyof typeof responsiveAnimations
} & ComponentProps<typeof Image>) => (
    <picture>
        <source media="(min-width: 1024px)" srcSet={responsiveAnimationsUrl(src).desktop} type="image/webp" />
        <source media="(min-width: 768px)" srcSet={responsiveAnimationsUrl(src).tablet} type="image/webp" />
        <source media="(max-width: 767px)" srcSet={responsiveAnimationsUrl(src).mobile} type="image/webp" />
        <Image src={responsiveAnimationsUrl(src).static} {...imgProps} />
    </picture>
)

/**
 * Build-time helper to create a data URL from SVG content with applied opacity.
 * Import the SVG as raw string using Vite's ?raw suffix, then use this function.
 *
 * @param svgContent - Raw SVG string content
 * @param opacity - Opacity value between 0 and 1
 * @returns Data URL with opacity applied
 *
 * @example
 * import acupOfJoeSvg from '../assets/Adverity-2023-Illustration_ACupOfJoe.svg?raw'
 * const svgWithOpacity = createSvgDataUrlWithOpacity(acupOfJoeSvg, 0.5)
 */
export const createSvgDataUrlWithOpacity = (svgContent: string, opacity: number): string => {
    // Add opacity attribute to root SVG element
    const svgWithOpacity = svgContent.replace(/<svg([^>]*)>/, `<svg$1 opacity="${opacity}">`)

    // Convert to data URL using btoa (safe in browser context)
    return `data:image/svg+xml;base64,${btoa(svgWithOpacity)}`
}
